import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { Loader2 } from "lucide-react";
import { PLUMBING_DEFAULT_LEVEL_CODE } from "@petergok/blue-collar-shared";
import referralTrialHero from "@/assets/images/referral-trial-hero.webp";
import { useRootStore } from "@/state/RootStore";
import { claimReferralTrial, dismissReferralOffer } from "@/api/fetch";
import { captureEvent } from "@/utils/posthog";
import { formatLong, formatTime } from "@/utils/date";
import { getCurrentLanguage } from "@/i18n";
import { APP_TO_BUILD } from "../../../appsConfig";
import { Button } from "@/components/ui/button";
import { getReferralTrialModalCopyKeys } from "@/referral/referralTrialMessaging";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useRedemptionPresentationBlocker } from "@/components/redemption/RedemptionPresentationContext";

interface TrialOfferModalProps {
  /**
   * The Coachmarks tour renders while this is true. The offer modal must never
   * stack on top of the tour, so the parent passes it through and we suppress
   * the modal until the tour is gone.
   */
  coachmarkActive: boolean;
}

const TrialOfferModal = observer(({ coachmarkActive }: TrialOfferModalProps) => {
  const { t, i18n } = useTranslation();
  const store = useRootStore();
  const flagEnabled = useFeatureFlagEnabled("refer-a-friend") === true;

  const [open, setOpen] = useState(true);
  const [pending, setPending] = useState<"accept" | "dismiss" | null>(null);
  const [error, setError] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  const offer = store.mainAppStore.referralOffer();
  const referralCode = offer?.code;
  const modalCopy = getReferralTrialModalCopyKeys(APP_TO_BUILD);

  const offerActive =
    flagEnabled &&
    store.mainAppStore.isReferralOfferClaimable() &&
    !store.mainAppStore.isReferralOfferDismissed() &&
    !store.mainAppStore.showCoachmark &&
    !coachmarkActive;

  // The reminder card sets this to re-open the modal after a prior dismiss.
  const forceOpen = store.mainAppStore.referralOfferModalForceOpen;

  // A successful claim flips `isReferralOfferClaimable` to false, so keep the
  // modal mounted through the in-flight claim and success state to avoid
  // unmounting before the user sees the confirmation.
  const shouldRender = offerActive || forceOpen || pending !== null || isSuccess;
  useRedemptionPresentationBlocker(
    "referral-offer",
    Boolean(shouldRender && open),
  );

  // The user dismissed earlier (local `open` is false), so re-arm it when the
  // reminder card requests a reopen.
  useEffect(() => {
    if (forceOpen) setOpen(true);
  }, [forceOpen]);

  const shownRef = useRef(false);
  useEffect(() => {
    if (offerActive && open && !shownRef.current) {
      shownRef.current = true;
      captureEvent("referral_offer_shown", {
        referral_code: referralCode,
        platform: "web",
      });
    }
  }, [offerActive, open, referralCode]);

  if (!shouldRender) return null;

  const language = i18n.language || getCurrentLanguage();

  // Closing always clears the reminder-card reopen flag so the card can
  // re-trigger the modal again later.
  const closeModal = () => {
    setOpen(false);
    store.mainAppStore.closeReferralOfferModal();
  };

  const handleAccept = async () => {
    setPending("accept");
    setError("");
    captureEvent("referral_offer_accepted", {
      referral_code: referralCode,
      platform: "web",
    });
    try {
      await claimReferralTrial();
      await store.mainAppStore.checkPurchaseState();
      captureEvent("referral_claim_success", {
        referral_code: referralCode,
        platform: "web",
      });
      setIsSuccess(true);
    } catch {
      captureEvent("referral_claim_fail", {
        referral_code: referralCode,
        platform: "web",
      });
      setError(t("referral.modal.error"));
    } finally {
      setPending(null);
    }
  };

  const handleDismiss = async () => {
    setPending("dismiss");
    setError("");
    captureEvent("referral_offer_dismissed", {
      referral_code: referralCode,
      platform: "web",
    });
    try {
      await dismissReferralOffer();
      await store.mainAppStore.checkPurchaseState();
    } catch {
      // Dismissal is best-effort; close regardless so the user isn't trapped.
    } finally {
      setPending(null);
      closeModal();
    }
  };

  const handleSuccessCta = async () => {
    try {
      if (APP_TO_BUILD === "plumbing") {
        await store.mainAppStore.setSelectedStateCode(PLUMBING_DEFAULT_LEVEL_CODE);
      }
    } finally {
      closeModal();
    }
  };

  const trialExpiresAt = store.mainAppStore.referralTrial()?.expiresAt;
  const expiryLabel =
    trialExpiresAt != null
      ? t(modalCopy.successExpiry, {
          date: formatLong(trialExpiresAt, language),
          time: formatTime(trialExpiresAt, language),
        })
      : "";

  return (
    <Dialog open={open} onOpenChange={(next) => !next && closeModal()}>
      <DialogContent className="sm:max-w-sm rounded-3xl p-0 overflow-hidden bg-white">
        <div className="px-6 py-8 text-center">
          <img
            src={referralTrialHero}
            alt={t("referral.modal.image_alt")}
            className="mx-auto mb-5 h-28 w-auto"
          />
          <DialogHeader>
            {/* The offer state has no visible title in the design (the mascot
                replaces it); keep the title for screen readers via sr-only. */}
            <DialogTitle
              className={cn(
                "text-center text-base font-bold text-[#232323]",
                !isSuccess && "sr-only",
              )}
            >
              {isSuccess
                ? t("referral.modal.success_title")
                : t("referral.modal.title")}
            </DialogTitle>
            <DialogDescription className="mx-auto mt-3 max-w-[18rem] text-center text-sm font-normal leading-6 text-[#666666]">
              {isSuccess ? expiryLabel : t(modalCopy.body)}
            </DialogDescription>
          </DialogHeader>

          {isSuccess ? (
            <Button
              type="button"
              onClick={handleSuccessCta}
              className="mt-7 h-auto w-full rounded-full bg-[var(--dp-brand-button)] py-3.5 text-base font-semibold text-white hover:bg-[var(--dp-brand-hover)]"
            >
              {t(modalCopy.successCta)}
            </Button>
          ) : (
            <div className="mt-7 space-y-3">
              {error && (
                <p role="alert" className="text-sm text-red-700">
                  {error}
                </p>
              )}
              <Button
                type="button"
                onClick={handleAccept}
                disabled={pending !== null}
                className={cn(
                  "h-auto w-full rounded-full bg-[var(--dp-brand-button)] py-3.5 text-base font-semibold text-white hover:bg-[var(--dp-brand-hover)]",
                  pending === "accept" && "bg-gray-200 hover:bg-gray-200 text-black",
                )}
              >
                {pending === "accept" && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {t("referral.modal.accept_cta")}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={handleDismiss}
                disabled={pending !== null}
                className="h-auto w-full rounded-full bg-[#EFF0EE] py-3.5 text-base font-semibold text-[#232323] hover:bg-[#E6E8E6] hover:text-[#232323]"
              >
                {pending === "dismiss" && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {t("referral.modal.dismiss_cta")}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
});

export default TrialOfferModal;
